import Box from '@mui/material/Box';
import {
	SxProps,
	Theme,
	TypographyVariant,
	useTheme
} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { HcpIcon } from '@/assets/icons/HcpIcon';
import { BasicChip } from '@/components/Chips/BasicChip';
import {
	AffiliationAddress,
	AffiliationAddressType
} from '@/components/Hcp/AffiliationAddress/AffiliationAddress';
import { Separated } from '@/components/Separated/Separated';
import { TEST_IDS } from '@/core/constants/app';
import { Hco, Hcp } from '@/core/graphql/generated/types';
import { mergeSx } from '@/utils/mergeSx';

import { ProfileName } from '../ProfileName/ProfileName';

import { useProfileMetaData } from './_hooks/useProfileMetaData';
import { hcpCardStyles as sx } from './HcpCard.styles';

export type CardView = 'default' | 'compact' | 'basic';

type Props = {
	hcp: Hcp;
	withIcon?: boolean;
	disableLink?: boolean;
	affiliationType?: AffiliationAddressType;
	affiliationOverride?: Hco | null;
	withFullAddress?: boolean;
	withoutCountry?: boolean;
	wrapChips?: boolean;
	chips?: ReactNode;
	chipLocation?: 'inline' | 'bottom';
	view?: CardView;
	variant?: TypographyVariant;
	query?: string;
	onClick?: () => void;
	onAffiliationClick?: () => void;
	customSx?: {
		root?: SxProps<Theme>;
		content?: SxProps<Theme>;
		name?: SxProps<Theme>;
	};
};

export const HcpCard = ({
	hcp,
	affiliationType = 'link',
	affiliationOverride,
	withIcon = false,
	disableLink = false,
	withFullAddress = false,
	withoutCountry = false,
	wrapChips,
	chips,
	chipLocation = 'inline',
	view = 'default',
	variant,
	query,
	onClick,
	onAffiliationClick,
	customSx = {}
}: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const {
		specialtyAndType,
		profileLink,
		primaryAffiliation: defaultPrimaryAffiliation
	} = useProfileMetaData(hcp);

	const primaryAffiliation =
		affiliationOverride !== undefined
			? affiliationOverride
			: defaultPrimaryAffiliation;

	const { getProfileName: getAffiliationProfileName } = useProfileMetaData(
		primaryAffiliation ?? null
	);

	const hasChips = Array.isArray(chips) ? chips.length > 0 : Boolean(chips);

	return (
		<Box
			sx={mergeSx(sx.root, customSx.root)}
			component={disableLink ? Box : Link}
			{...(!disableLink && { to: profileLink })}
			onClick={onClick}
			aria-label={t('Clickable HCP Search Card')}
			data-testid={TEST_IDS.search.hcpSearchCard}
		>
			{withIcon && (
				<Box sx={sx.iconContainer}>
					<HcpIcon fill={theme.palette.primary.main} />
				</Box>
			)}

			<Box sx={mergeSx(sx.mainContentContainer, customSx.content)}>
				<Box sx={sx.textRow}>
					<ProfileName
						profile={hcp}
						withDegrees
						withInternationalName
						inline
						withInactive={chipLocation === 'inline'}
						chips={chipLocation === 'inline' && chips}
						wrapChips={wrapChips}
						query={query}
						customSx={{ name: mergeSx(sx.name, customSx.name) }}
					/>
				</Box>

				{view === 'compact' && (
					<Separated
						layout='cut'
						separator='•'
						variant='body2'
						left={specialtyAndType}
						right={
							getAffiliationProfileName({ withInternationalName: true }).name
						}
					/>
				)}

				{view === 'default' && (
					<>
						{(specialtyAndType?.length ?? 0) > 0 && (
							<Box sx={sx.textRow}>
								<Typography variant='body2' sx={sx.specialtyAndType}>
									{specialtyAndType}
								</Typography>
							</Box>
						)}

						<AffiliationAddress
							primaryAffiliation={primaryAffiliation}
							addressView={withFullAddress ? 'full' : 'basic'}
							type={affiliationType}
							variant={variant}
							withoutCountry={withoutCountry}
							onNavigate={onAffiliationClick}
						/>

						{chipLocation === 'bottom' &&
							(hasChips || hcp.statusCda_VId === 'iact') && (
								<Box sx={sx.chipRow}>
									{chips}
									{hcp.statusCda_VId === 'iact' && (
										<BasicChip label={t('Inactive')} />
									)}
								</Box>
							)}
					</>
				)}
			</Box>
		</Box>
	);
};
